// src/utils/pollyService.js
// 🎵 완전한 로컬 음성 시스템 (AWS 완전 제거)

// 한글 직무명 → 영어 파일명 매핑 (실제 파일명에 맞춤)
const jobMapping = {
  "EHS": "EHS",
  "HRD/HRM": "HRD",
  "IT기획 (서비스/전략)": "itservice",
  "QA/QC": "QAQC",
  "UI/UX디자인": "UIUX",
  "개발자": "developer",
  "경영지원": "management",
  "고객상담": "CS",
  "간호사": "nurse",
  "공공기관 행정": "publicadmin",
  "공정기술": "process",
  "구매팀": "purchasing",
  "그래픽디자인": "graphic",
  "데이터분석": "data",
  "마케팅": "marketing",
  "무역사무,포워딩": "trade",
  "반도체엔지니어": "semiconductor",
  "반도체회로설계엔지니어": "circuit",
  "방송PD": "broadcast",
  "방송영상편집": "video",
  "법무": "legal",
  "사무행정": "admin",
  "사회복지사": "social",
  "상품기획/MD": "product",
  "생산관리": "production",
  "승무원": "flight",
  "시스템엔지니어": "system",
  "청소년활동가": "youth",
  "안전관리": "safety",
  "영업": "sales",
  "은행원": "teller",
  "유통관리": "logistics",
  "요양보호사": "care",
  "인사총무": "hradmin",
  "프로덕트 매니저(PM)": "pm",
  "재무": "finance",
  "정보보안": "security",
  "회계": "accounting"
};

// 🎶 로컬 오디오 재생 함수
const playLocalAudio = async (audioPath, callback) => {
  try {
    console.log(`🎵 로컬 오디오 재생: ${audioPath}`);
    
    const audio = new Audio(audioPath);
    audio.volume = 1.0;

    return new Promise((resolve, reject) => {
      audio.onended = () => {
        console.log(`✅ 오디오 재생 완료: ${audioPath}`);
        if (callback) {
          if (typeof callback === 'function') {
            callback(audio);
          }
        }
        resolve(audio);
      };

      audio.onerror = (error) => {
        console.error(`❌ 오디오 재생 오류: ${audioPath}`, error);
        if (callback) {
          if (typeof callback === 'function') {
            callback();
          }
        }
        reject(error);
      };

      audio.play().catch(reject);
    });

  } catch (error) {
    console.error('💥 로컬 오디오 함수 오류:', error);
    if (callback && typeof callback === 'function') {
      callback();
    }
    throw error;
  }
};

// 🎯 메인 함수 - 모든 음성을 로컬 파일로 처리
export const speakWithPolly = async (text, callback, mode = null, questionIndex = null, selectedJob = null) => {
  try {
    let audioPath = '';

    // 1️⃣ CountDown 음성 (mode가 없고 특정 텍스트인 경우)
    if (!mode && (
      text.includes('잠시후') || 
      text.includes('모의면접이 시작됩니다') ||
      text.includes('기본질문') ||
      text.includes('심화질문') ||
      text.includes('상황별') ||
      text.includes('직무별') ||
      text.includes('입시면접')  // 🆕 입시면접 추가!
    )) {
      audioPath = '/countdown.mp3';
      console.log('🎯 CountDown 음성 재생');
    }
    // 2️⃣ 기본질문 (default mode)
    else if (mode === 'default' && questionIndex !== null) {
      audioPath = `/audio/basic${questionIndex + 1}.mp3`;
      console.log(`🎯 기본질문 ${questionIndex + 1} 재생`);
    }
    // 3️⃣ 심화질문 (deep mode)
    else if (mode === 'deep' && questionIndex !== null) {
      audioPath = `/audio/deep${questionIndex + 1}.mp3`;
      console.log(`🎯 심화질문 ${questionIndex + 1} 재생`);
    }
    // 4️⃣ 상황질문 (situation mode)
    else if (mode === 'situation' && questionIndex !== null) {
      audioPath = `/audio/situation${questionIndex + 1}.mp3`;
      console.log(`🎯 상황질문 ${questionIndex + 1} 재생`);
    }
    // 🆕 5️⃣ 입시면접 (admission mode)
    else if (mode === 'admission' && questionIndex !== null) {
      audioPath = `/audio/admission${questionIndex + 1}.mp3`;
      console.log(`🎯 입시질문 ${questionIndex + 1} 재생`);
    }
    // 6️⃣ 직무질문 (custom mode)
    else if (mode === 'custom' && questionIndex !== null && selectedJob) {
      const englishJobName = jobMapping[selectedJob];
      if (englishJobName) {
        const paddedIndex = String(questionIndex + 1).padStart(2, '0');
        audioPath = `/job_audio/job_${englishJobName}_${paddedIndex}.mp3`;
        console.log(`🎯 직무질문 ${selectedJob} (${englishJobName}) ${questionIndex + 1} 재생`);
        console.log(`📁 파일 경로: ${audioPath}`);
      } else {
        console.error(`❌ 매핑되지 않은 직무: ${selectedJob}`);
        if (callback && typeof callback === 'function') {
          callback();
        }
        return;
      }
    }
    // 7️⃣ 기타 텍스트 (오디오 파일 없음)
    else {
      console.log(`⚠️ 오디오 파일이 없는 텍스트: ${text}`);
      if (callback && typeof callback === 'function') {
        callback();
      }
      return;
    }

    return await playLocalAudio(audioPath, callback);

  } catch (error) {
    console.error('💥 speakWithPolly 오류:', error);
    if (callback && typeof callback === 'function') {
      callback();
    }
  }
};

// 🧪 테스트 함수들
export const testLocalAudio = (mode = 'default', questionIndex = 0) => {
  console.log(`🧪 로컬 오디오 테스트: ${mode} 질문 ${questionIndex + 1}`);
  speakWithPolly('테스트', () => {
    console.log('✅ 로컬 오디오 테스트 완료!');
  }, mode, questionIndex);
};

export const testCountdownAudio = () => {
  console.log('🧪 CountDown 오디오 테스트');
  speakWithPolly('잠시후 모의면접이 시작됩니다', () => {
    console.log('✅ CountDown 오디오 테스트 완료!');
  });
};

export const testJobAudio = (jobName = '개발자', questionIndex = 0) => {
  console.log(`🧪 직무질문 오디오 테스트: ${jobName} 질문 ${questionIndex + 1}`);
  speakWithPolly('테스트', () => {
    console.log('✅ 직무질문 오디오 테스트 완료!');
  }, 'custom', questionIndex, jobName);
};

export const getAvailableJobs = () => {
  return Object.keys(jobMapping);
};

export const getJobFileName = (jobName) => {
  return jobMapping[jobName] || null;
};

export const testAWSConnection = async () => {
  console.log('🔇 AWS 연결 테스트 - 로컬 음성 시스템 사용중');
  return true;
};

export const koreanVoices = [
  { id: 'local', name: '로컬 음성 파일', gender: 'Custom' }
];

export const testPollyVoice = () => {
  testLocalAudio();
};